import { addHours } from "../utils/time";

export default function AutoOrganizeModal({ plan, players, onApply, onClose }) {
  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
  const nameOf = id => {
    const p = playerMap[id];
    if (!p) return id;
    return p.age != null ? `${p.name} (${p.age})` : p.name;
  };
  const moveFrom = Object.fromEntries(plan.moves.map(m => [m.playerId, m.fromGroupId]));

  const hasChanges = plan.groupSummaries.length > 0 || plan.deletions.length > 0;

  function annotate(playerId, groupId) {
    const from = moveFrom[playerId];
    if (from === undefined) return null; // unchanged, already in this group
    return from ? `moved from ${from}` : "previously unassigned";
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-800 text-base">Auto-Organize Proposal</h2>
            <p className="text-xs text-gray-500 mt-0.5">Review before applying — nothing changes until you confirm.</p>
          </div>
          <button onClick={onClose} className="text-xl text-gray-400 hover:text-gray-600 leading-none">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {!hasChanges && plan.unplaced.length === 0 && (
            <p className="text-sm text-gray-400 italic">Nothing to reorganize.</p>
          )}

          {plan.groupSummaries.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {plan.groupSummaries.some(g => g.isNew) ? "New & updated groups" : "Updated groups"}
              </div>
              <div className="space-y-2">
                {plan.groupSummaries.map(g => (
                  <div key={g.groupId} className="border border-gray-200 rounded-lg px-3 py-2">
                    <div className="text-sm font-mono font-bold text-gray-800">
                      {g.groupId} <span className="font-sans font-normal text-gray-500">· {g.tier}</span>
                      {g.isNew && <span className="ml-1.5 text-xs font-sans font-semibold text-blue-600">NEW</span>}
                    </div>
                    {g.day && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {g.day} · {g.startTime}–{addHours(g.startTime, g.duration)} · {g.court}
                      </div>
                    )}
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {g.playerIds.map(pid => {
                        const note = annotate(pid, g.groupId);
                        return (
                          <li key={pid}>
                            {nameOf(pid)}
                            {note && <span className="text-gray-400"> — {note}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan.deletions.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Groups to delete (now empty)
              </div>
              <div className="space-y-1.5">
                {plan.deletions.map(d => (
                  <div key={d.groupId} className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2">
                    <span className="font-mono font-bold text-gray-700">{d.groupId}</span>
                    <span className="text-gray-500"> · {d.tier}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan.unplaced.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Could not place
              </div>
              <div className="space-y-1.5">
                {plan.unplaced.map(u => (
                  <div key={u.player.id} className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <span className="font-medium text-amber-800">{nameOf(u.player.id)}</span>
                    <span className="text-amber-700"> — {u.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan.skipped.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Skipped (no availability listed)
              </div>
              <p className="text-xs text-gray-500">
                {plan.skipped.map(s => nameOf(s.player.id)).join(", ")}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {hasChanges && (
            <button
              onClick={onApply}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
            >
              Apply
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
